import { useState, useCallback, useRef } from 'react';
import { Upload, Trash2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { cn } from '../../../lib/utils';
import type { BlogFont } from '../../../types';

interface FontManagerProps {
  fonts: BlogFont[];
  onUploadFont: (file: File, family: string) => Promise<void>;
  onDeleteFont: (id: string) => Promise<void>;
}

export function FontManager({ fonts, onUploadFont, onDeleteFont }: FontManagerProps) {
  const [family, setFamily] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    if (file && !family) {
      // Auto-fill family from filename (strip extension)
      const guessed = file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ');
      setFamily(guessed);
    }
  }, [family]);

  const handleUpload = useCallback(async () => {
    if (!selectedFile || !family.trim()) return;
    setIsUploading(true);
    try {
      await onUploadFont(selectedFile, family.trim());
      setFamily('');
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } finally {
      setIsUploading(false);
    }
  }, [selectedFile, family, onUploadFont]);

  const handleDelete = useCallback(async (id: string) => {
    setDeletingId(id);
    try {
      await onDeleteFont(id);
    } finally {
      setDeletingId(null);
    }
  }, [onDeleteFont]);

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium">Fontes Personalizadas</h3>

      {/* Upload form */}
      <div className="border border-border rounded-lg p-4 space-y-3">
        <p className="text-xs text-muted-foreground">
          Carregue fontes .ttf, .woff ou .woff2 para usar no editor de posts.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".ttf,.woff,.woff2,.otf"
              className="hidden"
              onChange={handleFileChange}
              id="font-file-input"
            />
            <label htmlFor="font-file-input">
              <Button
                type="button"
                size="sm"
                variant="outline"
                asChild
                disabled={isUploading}
              >
                <span className="cursor-pointer">
                  <Upload className="w-4 h-4 mr-1" />
                  {selectedFile ? selectedFile.name : 'Selecionar arquivo'}
                </span>
              </Button>
            </label>
          </div>
          <Input
            value={family}
            onChange={(e) => setFamily(e.target.value)}
            placeholder="Nome da família (ex: Roboto)"
            className="h-8 text-sm flex-1 max-w-xs"
            disabled={isUploading}
          />
          <Button
            size="sm"
            onClick={handleUpload}
            disabled={isUploading || !selectedFile || !family.trim()}
          >
            {isUploading ? 'Enviando...' : 'Enviar'}
          </Button>
        </div>
      </div>

      {/* Font list */}
      {fonts.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhuma fonte carregada.</p>
      ) : (
        <ul className="divide-y divide-border border border-border rounded-lg overflow-hidden">
          {fonts.map((font) => (
            <FontRow
              key={font.id}
              font={font}
              isDeleting={deletingId === font.id}
              onDelete={() => handleDelete(font.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

interface FontRowProps {
  font: BlogFont;
  isDeleting: boolean;
  onDelete: () => void;
}

function FontRow({ font, isDeleting, onDelete }: FontRowProps) {
  // Inject a @font-face style for this font so we can preview it
  const previewStyle =
    font.source === 'CUSTOM' && font.url
      ? { fontFamily: `"${font.family}"` }
      : { fontFamily: font.family };

  return (
    <li className="flex items-center justify-between gap-4 px-4 py-3">
      <div className="flex items-center gap-3 min-w-0">
        <Badge variant={font.source === 'GOOGLE' ? 'secondary' : 'outline'} className="shrink-0">
          {font.source === 'GOOGLE' ? 'Google' : 'Custom'}
        </Badge>
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{font.family}</p>
          <p
            className="text-sm text-muted-foreground truncate"
            style={previewStyle}
          >
            Acrílico cristal transparente 3mm
          </p>
        </div>
      </div>
      <Button
        size="sm"
        variant="ghost"
        className={cn(
          'h-7 w-7 p-0 shrink-0',
          'hover:text-destructive hover:bg-destructive/10'
        )}
        onClick={onDelete}
        disabled={isDeleting}
        aria-label={`Remover fonte ${font.family}`}
      >
        <Trash2 className="w-3 h-3" />
      </Button>
    </li>
  );
}
